import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import FormControl from '@material-ui/core/FormControl';
import ListItemText from '@material-ui/core/ListItemText';
import Select from '@material-ui/core/Select';
import Checkbox from '@material-ui/core/Checkbox';
import MenuItem from '@material-ui/core/MenuItem';
import Actions_Wind_V2 from '~/Actions_Wind_V2';
import LoadingOverlay from '~/components/LoadingOverlay';

import { T_TableRow, T_TitleCell } from '~/components/TableItem';
import { S_Btn_Search, S_DateInput, S_Select, S_Title } from '~/components/SearchItem';
import CustomItemPropSelect from './CustomItemPropSelect';

const RenderTableTitle = (props) => {
  const { sort, sort_column_name, onSort } = props;
  const [ary, setAry] = useState([
    { name: '港口', id: 'dock', type: 'string', order: false }, // true: 'desc', false: 'asc'
    { name: '碼頭後線土地', id: 'near_land', type: 'string', order: true },
    { name: '使用風場', id: 'windfield_simplename', type: 'string', order: true },
    { name: '存放部件', id: 'store_part', type: 'string', order: true },
    { name: '數量', id: 'item_num', type: 'number', order: true },
    { name: '預計存放空間需求', id: 'expect_space', type: 'string', order: true },
    { name: '出租方', id: 'lessor', type: 'string', order: true },
    { name: '租期起始', id: 'rent_start_date', type: 'string', order: true },
    { name: '租期結束', id: 'rent_end_date', type: 'string', order: true },
  ]);

  const onChangeSort = (target) => {
      let method = 'asc';
      let type = 'string';
      const updatedItems = ary.map((item) => {
          if (item.id === target) {
              method = !item.order ? 'desc' : 'asc';
              type = item.type;
              return { ...item, order: !item.order };
          }
          return { ...item, order: true };
      });

      setAry(updatedItems);
      onSort(target, method, type);
  };

  return (
      <TableHead>
          <TableRow style={{ height: 48 }}>
              {ary.map((o, i) => (
                  <T_TitleCell
                      key={i}
                      text={o.name}
                      arrow={!!o.id}
                      bol={o.order}
                      onClick={() => onChangeSort(o.id)}
                  />
              ))}
          </TableRow>
      </TableHead>
  );
};

const RenderTableBody = React.memo(
  (props) => {
    const { list } = props;
    const cellStyle = { fontSize: 14 };
  
    const lessor = t => {
      try {
        const obj = JSON.parse(t);
        return `${obj[0].a}${obj[0].b}`;
      } catch (err) {
        return '';
      }
    };
  
    return (
      <TableBody>
        {list.map((o, i) => (
          <T_TableRow key={i}>
            <TableCell align='center' style={cellStyle}>{o.dock}</TableCell>
            <TableCell align='center' style={cellStyle}>{(o.near_land === '其他') ? `${o.near_land}_${o.near_land_other}` : o.near_land}</TableCell>
            <TableCell align='center' style={cellStyle}>{o.windcontract_data?.windfield_simplename}</TableCell>
            <TableCell align='center' style={cellStyle}>{o.store_part}</TableCell>
            <TableCell align='center' style={cellStyle}>{o.item_num}</TableCell>
            <TableCell align='center' style={cellStyle}>{o.expect_space}</TableCell>
            <TableCell align='center' style={cellStyle}>{lessor(o.lessor)}</TableCell>
            <TableCell align='center' style={cellStyle}>{o.rent_start_date}</TableCell>
            <TableCell align='center' style={cellStyle}>{o.rent_end_date}</TableCell>
          </T_TableRow>
        ))}
      </TableBody>
    );
  }
);

const RenderTable = React.memo(
  (props) => {
    const { list, onSort } = props;
    const haveData = list.length > 0;
    return (
      <TableContainer>
        <Table style={{ backgroundColor: 'white', borderRadius: 8, borderTopLeftRadius: 0 }}>
          <RenderTableTitle onSort={onSort} />
          {haveData && <RenderTableBody list={list} />}
        </Table>
      </TableContainer>
    );
  }
);

const App = () => {
  const dispatch = useDispatch();
  const [selectedDocks, setSelectedDocks] = useState([]);
  const [selectedNearLands, setSelectedNearLands] = useState([]);
  const [selectedWindContracts, setSelectedWindContracts] = useState([]);
  const [selectedStoreParts, setSelectedStoreParts] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const docks = useSelector(state => state.data.AllDocks || []);
  const nearLands = useSelector(state => state.data.AllNearLands || []);
  const windContracts = useSelector(state => state.data.AllWindContracts || []);
  const storeParts = useSelector(state => state.data.AllStoreParts || []);
  const [sortConfig, setSortConfig] = useState({
    sortBy: 'dock',
    order: 'asc',
    type: 'string'
  });


  const haveData = list.length > 0;

  useEffect(() => {
    Actions_Wind_V2.Gis.getAllDocks(dispatch);
    Actions_Wind_V2.Gis.getAllNearLands(dispatch);
    Actions_Wind_V2.Gis.getAllWindContracts(dispatch);
    Actions_Wind_V2.Gis.getAllStoreParts(dispatch);
  }, [dispatch]);

  const handleChangeSelection = (e, type) => {
    if (type == 'dock') {
      setSelectedDocks(e.target.value);
    } else if (type == 'nearLand') {
      setSelectedNearLands(e.target.value);
    } else if (type == 'windContract') {
      setSelectedWindContracts(e.target.value);
    } else if (type == 'storePart') {
      setSelectedStoreParts(e.target.value);
    } else {
      console.log('no correct type...');
    }
  };

  const onClickSearch = () => {
    setLoading(true);
    const dock_ids = selectedDocks.map(o => o.value);
    const nearLand_ids = selectedNearLands.map(o => o.value);
    const windContract_ids = selectedWindContracts.map(o => o.value);
    const storePart_ids = selectedStoreParts.map(o => o.value);
    
    if (dock_ids.length > 0  && nearLand_ids.length > 0  && windContract_ids.length > 0  && storePart_ids.length > 0) {
      Actions_Wind_V2.Gis.getStorePartDockInfo('3', {
        docks: dock_ids,
        near_lands: nearLand_ids,
        store_parts: storePart_ids,
        windcontract_names: windContract_ids
      }).then(res => {
        console.log(res);
        let newList = res.map(item => {
          const { rent_end_date = '', windcontract_data: { windfield_simplename = '' } = {} } = item;
          return { ...item, rent_end_date: rent_end_date, windfield_simplename: windfield_simplename || '' };
        });
        
        handleLocalListSort(newList, sortConfig);
      }).finally(() => {
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  };

  const handleDataProcessing = () => {

  }

  const handleSort = (sortBy, order, type) => {
    console.log('sort', sortBy, order, type);
    const newSortConfig = { sortBy, order, type };
    setSortConfig({ sortBy: sortBy, order: order, type: type });
    handleLocalListSort(list, newSortConfig);
  };

  const handleLocalListSort = (data, config) => {

    console.log(data, config);


    const type = config['type'];
    const property = config['sortBy'];
    const order = config['order'];

    const sortedData = [...data].sort((a, b) => {
      if (type === 'string') {
        const comparison = a[property].localeCompare(b[property]);
        return order === 'desc' ? -comparison : comparison;
      } else if (type === 'number') {
        const aValue = a[property] === '' ? Number.MIN_SAFE_INTEGER : Number(a[property]);
        const bValue = b[property] === '' ? Number.MIN_SAFE_INTEGER : Number(b[property]);
  
        const comparison = aValue - bValue;
        return order === 'desc' ? -comparison : comparison;
      }
      return 0;
    });

    setList(sortedData);
  };

  return (
    <>
      {isLoading && <LoadingOverlay loading={isLoading} />}
      <div style={{ marginLeft: 52, marginRight: 52, marginTop: 20, marginBottom: 20 }}>
        <div style={{ marginTop: 24, display: 'flex', marginBottom: 24 }}>
          <CustomItemPropSelect
            label="港口"
            value={selectedDocks}
            onChange={handleChangeSelection}
            options={docks}
            selectedItems={selectedDocks}
            type="dock"
          />

          <CustomItemPropSelect
            label="碼頭後線土地"
            value={selectedNearLands}
            onChange={handleChangeSelection}
            options={nearLands}
            selectedItems={selectedNearLands}
            type="nearLand"
          />

          <CustomItemPropSelect
            label="使用風場"
            value={selectedWindContracts}
            onChange={handleChangeSelection}
            options={windContracts}
            selectedItems={selectedWindContracts}
            type="windContract"
          />

          <CustomItemPropSelect
            label="存放部件"
            value={selectedStoreParts}
            onChange={handleChangeSelection}
            options={storeParts}
            selectedItems={selectedStoreParts}
            type="storePart"
          />
          <div style={{ marginLeft: 16, display: 'flex', flexDirection: 'column-reverse' }}>
            <S_Btn_Search onClick={onClickSearch} />
          </div>
        </div>

        <div style={{ display: 'flex' }}>
          <div style={ {
            backgroundColor: '#4CC2CA',
            height: 50,
            borderTopLeftRadius: 11,
            position: 'relative',
          }}
          >
            <div style={{ lineHeight: ' 50px', color: 'white', fontWeight: 700, fontSize: 20, marginLeft: 8, marginRight: 20 }}>零組件存放碼頭</div>
            <div
              style={{
                backgroundColor: '#4CC2CA',
                height: 50,
                width: 40,
                clipPath: 'polygon(0% 100%, 0% 0%, 30% 0%, 100% 100%)',
                position: 'absolute',
                top: 0,
                left: ' calc(100% - 1px)',
              }}
            />
          </div>
        </div>
        <div style={{ borderRadius: 8, boxShadow: '0px 4px 13px rgba(24, 98, 121, 0.4)' }}>
          <RenderTable list={list} onSort={handleSort} />
          {/* {haveData && this.renderTableBar()} */}
        </div>
        {!haveData && <div style={{ width: '100%', display: 'flex', justifyContent: 'center', marginTop: 30 }}>尚無資料</div>}
      </div>
    </>
  );
};

export default App;
